import { useMemo, useState } from "react";
import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";
import { Bell, Cake, Clock3, Loader2, PhoneCall } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMarkAllNotificationsRead,
  useNotifications,
  useUnreadNotificationsCount,
} from "@/hooks/notifications/useNotifications";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type {
  KnownPortalNotificationType,
  PortalNotificationDO,
} from "@/types/notification";

type NotificationCategory =
  | "birthday"
  | "subscription_trial_expiry"
  | "prospect_follow_up"
  | "other";
type NotificationFilter = "all" | NotificationCategory;

const notificationLabels: Record<NotificationFilter, string> = {
  all: "All",
  birthday: "Birthday",
  subscription_trial_expiry: "Subscription/trial expiry",
  prospect_follow_up: "Prospect follow up",
  other: "Other",
};

const notificationStyles: Record<NotificationCategory, string> = {
  birthday:
    "border-[hsl(38_92%_50%/0.34)] bg-[linear-gradient(135deg,hsl(38_92%_50%/0.18),hsl(38_92%_50%/0.08))]",
  subscription_trial_expiry:
    "border-[hsl(var(--primary)/0.3)] bg-[linear-gradient(135deg,hsl(var(--primary)/0.18),hsl(var(--primary)/0.08))]",
  prospect_follow_up:
    "border-[hsl(144_61%_30%/0.25)] bg-[linear-gradient(135deg,hsl(144_61%_46%/0.15),hsl(144_61%_28%/0.06))]",
  other:
    "border-border bg-[linear-gradient(135deg,hsl(var(--muted)),hsl(var(--muted)/0.45))]",
};

const notificationIconStyles: Record<NotificationCategory, string> = {
  birthday:
    "border border-[hsl(38_92%_50%/0.38)] bg-[hsl(38_92%_50%/0.18)] text-[hsl(32_95%_32%)]",
  subscription_trial_expiry:
    "border border-[hsl(var(--primary)/0.35)] bg-[hsl(var(--primary)/0.16)] text-[hsl(var(--primary))]",
  prospect_follow_up:
    "border border-[hsl(144_61%_30%/0.26)] bg-[hsl(144_61%_46%/0.15)] text-[hsl(144_61%_24%)]",
  other: "border border-border bg-muted text-muted-foreground",
};

const readNotificationIconStyles: Record<NotificationCategory, string> = {
  birthday: "border-border bg-muted text-muted-foreground",
  subscription_trial_expiry: "border-border bg-muted text-muted-foreground",
  prospect_follow_up: "border-border bg-muted text-muted-foreground",
  other: "border-border bg-muted text-muted-foreground",
};

const unreadIndicatorStyles: Record<NotificationCategory, string> = {
  birthday:
    "border border-[hsl(38_92%_50%/0.45)] bg-[hsl(38_92%_50%/0.95)] shadow-[0_0_0_3px_hsl(38_92%_50%/0.14)]",
  subscription_trial_expiry:
    "border border-[hsl(var(--primary)/0.45)] bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.14)]",
  prospect_follow_up:
    "border border-[hsl(144_61%_30%/0.4)] bg-[hsl(144_61%_30%/0.92)] shadow-[0_0_0_3px_hsl(144_61%_30%/0.14)]",
  other:
    "border border-border bg-muted-foreground shadow-[0_0_0_3px_hsl(var(--muted))]",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>("all");
  const navigate = useNavigate();
  const { isAuthenticated, isAuthReady, selectedBranchId } = useAuth();
  const isQueryEnabled = isAuthReady && isAuthenticated;
  const unreadCountQuery = useUnreadNotificationsCount(
    isQueryEnabled,
    selectedBranchId,
  );
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useNotifications(isQueryEnabled && open, selectedBranchId);
  const markAllReadMutation = useMarkAllNotificationsRead(selectedBranchId);

  const allNotifications = useMemo(
    () =>
      [...(data?.notifications ?? [])].sort(
        (left, right) =>
          parseISO(right.createdAt).getTime() - parseISO(left.createdAt).getTime(),
      ),
    [data?.notifications],
  );
  const filteredNotifications = useMemo(
    () =>
      activeFilter === "all"
        ? allNotifications
        : allNotifications.filter(
            (notification) =>
              getNotificationCategory(notification.type) === activeFilter,
          ),
    [activeFilter, allNotifications],
  );
  const unreadCount =
    data?.unreadCount ?? unreadCountQuery.data?.unreadCount ?? 0;
  const shouldShowBadge = unreadCount > 0;

  const handleMarkAllAsRead = () => {
    if ((data?.unreadCount ?? 0) === 0 || markAllReadMutation.isPending) {
      return;
    }

    markAllReadMutation.mutate(undefined, {
      onError: (mutationError) => {
        toast.error(
          mutationError.message || "Failed to mark notifications as read.",
        );
      },
    });
  };

  const filterOptions = getFilterOptions(allNotifications);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 sm:h-9 sm:w-9"
          aria-label="Notifications"
        >
          <Bell className="h-4.5 w-4.5" />
          {shouldShowBadge && (
            <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full p-0 sm:max-w-[440px] lg:max-w-[480px]"
      >
        <SheetHeader className="border-b px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="space-y-1">
              <SheetTitle className="text-base">Notifications</SheetTitle>
              <p className="text-sm text-muted-foreground">
                {allNotifications.length > 0
                  ? `${allNotifications.length} notification${allNotifications.length === 1 ? "" : "s"}`
                  : "All caught up"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {markAllReadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0 || markAllReadMutation.isPending}
              >
                Mark all as read
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="border-b px-4 py-3 sm:px-5">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {filterOptions.map((option) => {
              const isActive = activeFilter === option.value;

              return (
                <Button
                  key={option.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  className="h-8 rounded-full px-3"
                  onClick={() => setActiveFilter(option.value)}
                >
                  {notificationLabels[option.value]}
                  <Badge
                    variant={isActive ? "secondary" : "outline"}
                    className={cn(
                      "ml-2 min-w-5 justify-center rounded-full px-1.5 py-0 text-[10px]",
                      isActive
                        ? "border-transparent bg-background/90 text-foreground"
                        : "bg-background",
                    )}
                  >
                    {option.count}
                  </Badge>
                </Button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Notifications older than 7 days are automatically deleted.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-3 p-5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        ) : isError ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm font-medium text-foreground">
              Unable to load notifications
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {error.message}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => void refetch()}
            >
              Retry
            </Button>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {activeFilter === "all"
                ? "No notifications right now"
                : `No ${notificationLabels[activeFilter].toLowerCase()} notifications right now`}
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100dvh-13rem)] sm:h-[calc(100dvh-11rem)]">
            <div className="space-y-3 p-4 sm:p-5">
              {filteredNotifications.map((notification) => (
                <NotificationListItem
                  key={notification._id}
                  notification={notification}
                  onNavigate={(path, draft) => {
                    setOpen(false);
                    navigate(path, {
                      state: draft ? { notificationDraft: draft } : null,
                    });
                  }}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </SheetContent>
    </Sheet>
  );
}

function NotificationListItem({
  notification,
  onNavigate,
}: {
  notification: PortalNotificationDO;
  onNavigate: (path: string, draft?: { memberId: string; text: string }) => void;
}) {
  const category = getNotificationCategory(notification.type);
  const Icon = getNotificationIcon(category);
  const createdAtLabel = getNotificationTimestamp(notification.createdAt);
  const actions = getNotificationActions(notification);

  return (
    <div
      className={cn(
        "rounded-xl border p-3 transition-colors",
        notification.isRead
          ? "border-border bg-muted/30 shadow-none"
          : notificationStyles[category],
        notification.isRead
          ? "opacity-100"
          : "shadow-[0_10px_24px_-16px_rgba(15,23,42,0.45)]",
      )}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            notification.isRead
              ? readNotificationIconStyles[category]
              : notificationIconStyles[category],
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p
              className={cn(
                "min-w-0 text-sm leading-tight",
                notification.isRead
                  ? "font-medium text-foreground/75"
                  : "font-semibold text-foreground",
              )}
            >
              {notification.title}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground/90">
                {createdAtLabel}
              </p>
              {!notification.isRead ? (
                <span
                  className={cn(
                    "h-2.5 w-2.5 shrink-0 rounded-full",
                    unreadIndicatorStyles[category],
                  )}
                />
              ) : null}
            </div>
          </div>
          <p
            className={cn(
              "mt-1 text-xs leading-relaxed",
              notification.isRead
                ? "text-muted-foreground"
                : "text-foreground/80",
            )}
          >
            {notification.message}
          </p>
          {actions.length > 0 ? (
            <div
              className="mt-3 flex flex-wrap gap-2"
              role="group"
              aria-label="Notification actions"
            >
              {actions.map((action) => (
                <Button
                  key={action.label}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onNavigate(action.path, action.draft)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function getNotificationActions(notification: PortalNotificationDO) {
  const meta = notification.meta;
  const actions: Array<{
    label: string;
    path: string;
    draft?: { memberId: string; text: string };
  }> = [];

  if (notification.type === "birthday") {
    if (meta?.timing === "3_days_before") {
      actions.push({
        label: "Prepare announcement",
        path: "/broadcast?tab=announcements",
      });
    } else if (meta?.timing === "today") {
      actions.push({
        label: "Send announcement",
        path: "/broadcast?tab=announcements",
      });

      const memberId =
        typeof meta.memberId === "string" ? meta.memberId.trim() : "";
      if (memberId.length > 0) {
        const memberName =
          typeof meta.memberName === "string" ? meta.memberName.trim() : "";
        const firstName = memberName.split(/\s+/)[0] || "there";
        actions.push({
          label: "Message member",
          path: `/broadcast?tab=messages&memberId=${encodeURIComponent(memberId)}`,
          draft: {
            memberId,
            text: `Happy birthday, ${firstName} 🎉! We love and appreciate you. Have a wonderful day!`,
          },
        });
      }
    }
  } else if (
    notification.type === "trial" ||
    notification.type === "portal_subscription"
  ) {
    actions.push({
      label: "View FirstRep Billing",
      path: "/settings?section=subscription",
    });
  } else if (notification.type === "prospect_follow_up") {
    actions.push({ label: "Review prospects", path: "/growth" });
  }

  return actions;
}

function getNotificationCategory(
  type: PortalNotificationDO["type"],
): NotificationCategory {
  switch (type as KnownPortalNotificationType) {
    case "birthday":
      return "birthday";
    case "prospect_follow_up":
    case "trial":
    case "portal_subscription":
      return type === "prospect_follow_up"
        ? "prospect_follow_up"
        : "subscription_trial_expiry";
    default:
      return "other";
  }
}

function getNotificationIcon(category: NotificationCategory) {
  switch (category) {
    case "birthday":
      return Cake;
    case "prospect_follow_up":
      return PhoneCall;
    case "subscription_trial_expiry":
    case "other":
      return Clock3;
  }
}

function getFilterOptions(notifications: PortalNotificationDO[]) {
  const counts = notifications.reduce<Record<NotificationCategory, number>>(
    (accumulator, notification) => {
      accumulator[getNotificationCategory(notification.type)] += 1;
      return accumulator;
    },
    {
      birthday: 0,
      subscription_trial_expiry: 0,
      prospect_follow_up: 0,
      other: 0,
    },
  );

  const options = [
    {
      value: "all" as const,
      count: notifications.length,
    },
    {
      value: "birthday" as const,
      count: counts.birthday,
    },
    {
      value: "subscription_trial_expiry" as const,
      count: counts.subscription_trial_expiry,
    },
    {
      value: "prospect_follow_up" as const,
      count: counts.prospect_follow_up,
    },
  ];

  if (counts.other > 0) {
    options.push({
      value: "other" as const,
      count: counts.other,
    });
  }

  return options;
}

function getNotificationTimestamp(createdAt: string) {
  const parsedDate = parseISO(createdAt);

  if (!isValid(parsedDate)) {
    return "";
  }

  const ageInDays = (Date.now() - parsedDate.getTime()) / (1000 * 60 * 60 * 24);

  if (ageInDays >= 6) {
    return format(parsedDate, "MMM d");
  }

  return `${formatDistanceToNowStrict(parsedDate)} ago`;
}
